import {
  ActionIcon,
  AppShell,
  Button,
  Flex,
  Group,
  Indicator,
  Loader,
  Menu,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { INotification } from "@mctypes/global.types";
import { IMaconomyProject, IProject } from "@schemas/projectSchema";
import {
  IconArchive,
  IconArchiveFilled,
  IconBrandTeams,
  IconBriefcaseFilled,
  IconFileCheck,
  IconHelpCircle,
  IconLogout,
  IconMail,
  IconMailOpened,
  IconSettings,
  IconSocial,
  IconX,
} from "@tabler/icons-react";
import { useState } from "react";
import { useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import { useGetProjectsMaconomyQuery, useGetProjectsMcappsQuery } from "../../openapi";
import { Avatar } from "../components";
import ContactForm from "../components/ContactForm/ContactForm";
import { McSelect } from "../components/McSelect/McSelect";
import NotificationCard from "../components/NotificationCard/NotificationCard";
import SidePanel from "../components/SidePanel/SidePanel";
import TaskBanner from "../components/TaskBanner/TaskBanner";
import {
  addAppModalThunk,
  logoutThunk,
  selectIsUserMutilconsult,
  setShowNotificationsInRedux,
  setShowSuccessMessageThunk,
  thunkReloadAppThunk,
  toggleAllNotificationsAsReadThunk,
  toggleNotificationAndReplaceNotificationThunk,
} from "../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../hooks";
import { AppModalEnums } from "../mc-shared/sharedConstants";
import appShellCss from "./McAppShell.module.css";
import menuCss from "./McAppshellMenu.module.css";
import { NotificationButton } from "./NotificationsButton";
import { selectMergedProjectList } from "./projectSelectors";
import ReleaseNotesButton from "./ReleaseNotesButton";
import { doCreateOrJoinProject, doSendUserMessage } from "./routes/admin/restQueries";
import { isLoggedinUserMulticonsultUser } from "./routes/admin/selectors";
import UserSettingsModalContent from "./user/UserSettingsModalContent";

type TMcAppShell = (props: { useShell?: boolean; children: React.ReactNode }) => JSX.Element;

export const McAppShell: TMcAppShell = ({ children }) => {
  const dispatch = useAppDispatch();
  const showNotification = useAppSelector((state) => state.globalReducer.showNotifications);
  const [showMarkedAsRead, setShowMarkedAsRead] = useLocalStorage("showMarkedAsRead", true);
  const user = useAppSelector((state) => state.globalReducer.user);
  let history = useHistory();

  const toggleMarkedAsRead = (notification: INotification) => {
    dispatch(toggleNotificationAndReplaceNotificationThunk(notification._id));
  };

  const checkCondition = (notification: INotification) => {
    return showMarkedAsRead === true ? true : !notification.markedAsRead.value;
  };

  const markAllNotificationsAsRead = () => {
    dispatch(toggleAllNotificationsAsReadThunk());
  };

  const openModalAndMarkAsRead = (notification: INotification) => {
    const _project = notification.project as IProject;
    if (notification.markedAsRead?.value === false) {
      dispatch(toggleNotificationAndReplaceNotificationThunk(notification._id));
    }
    if (notification.app === "DASHBOARD") {
      history.push(`/project/${_project._id}/dashboard/?layout=${notification.itemDBid}`);
      return;
    }
    if (notification.app === "MEETING") {
      history.push(`/project/${_project._id}/meetings/${notification.boardId}`);
      return;
    }

    if (notification.app === "CHECKLIST") {
      history.push(`/project/${_project._id}/checklist/instance/${notification.boardId}?id=${notification.itemDBid}`);
      return;
    }
    dispatch(
      addAppModalThunk({
        itemId: notification.itemDBid,
        boardId: notification.boardId,
        projectId: _project._id,
        app: AppModalEnums.ISSUE,
      }),
    );
  };

  const hideAppShell = history.location.search.includes("doksam=true");

  return hideAppShell === true ? (
    <>{children}</>
  ) : (
    <>
      <AppShell h="100dvh">
        <AppShell.Header display="flex" bg={"dark.7"}>
          <AppHeader />
        </AppShell.Header>
        <AppShell.Main h="calc(100dvh - var(--app-shell-header-height, 60px))" className="overflow-hidden">
          <Stack h="100%" gap={0} bg="white">
            {!window.location.href.replace(/\/+$/, "").endsWith("tasks") &&
              user?.activeAssignedItemsCount?.overdue > 0 && (
                <TaskBanner
                  userName={user.surname ? user.surname : user.name}
                  overdue={user.activeAssignedItemsCount?.overdue}
                />
              )}
            <Flex gap={0} h="100%" className="overflow-auto">
              {children}
            </Flex>
            <DevBanner />
          </Stack>
        </AppShell.Main>
      </AppShell>
      <SidePanel collapsed={!showNotification}>
        <Flex justify="space-between">
          <Tooltip openDelay={500} label="Skjul notifikasjoner" withArrow>
            <Button
              variant="outline"
              onClick={() => {
                dispatch(setShowNotificationsInRedux(false));
              }}
            >
              <IconX size={20} />
            </Button>
          </Tooltip>
          <Flex gap="xs">
            <Tooltip openDelay={500} label="Sett alle notifikasjoner som lest" withArrow>
              <Button variant="outline" onClick={() => markAllNotificationsAsRead()}>
                <IconMailOpened size={20} />
              </Button>
            </Tooltip>
            <Tooltip openDelay={500} label="Vis / skjul leste meldinger" withArrow>
              <Button variant="outline" onClick={() => setShowMarkedAsRead(!showMarkedAsRead)}>
                {showMarkedAsRead ? <IconArchiveFilled size={20} /> : <IconArchive size={20} />}
              </Button>
            </Tooltip>
          </Flex>
        </Flex>
        <Stack h="100%" gap="xs" mt="xs" className="overflow-auto">
          {user?.notifications?.map((notification, index) => {
            return (
              checkCondition(notification) && (
                <NotificationCard
                  key={index}
                  notification={notification}
                  onCardClick={(notification) => openModalAndMarkAsRead(notification)}
                  toggleMarkedAsRead={toggleMarkedAsRead}
                />
              )
            );
          })}
        </Stack>
      </SidePanel>
    </>
  );
};

export const DevBanner = () => {
  const isInDev = import.meta.env.DEV;

  if (isInDev === false) {
    return null;
  }

  const dispatch = useAppDispatch();
  const [showDevBanner, setShowDevBanner] = useState(true);

  if (showDevBanner) {
    return (
      <Group bg="red" justify="space-between" align="center" p="xs">
        <Button color="green" variant="filled" onClick={() => dispatch(thunkReloadAppThunk(history))}>
          Nullstill app state
        </Button>

        <Text c="white" fw={700} fz="xl">
          TESTMILJØ
        </Text>

        <ActionIcon variant="subtle" color="white" onClick={() => setShowDevBanner(false)} aria-label="Lukk banner">
          <i className="fa fa-close fa-fw" />
        </ActionIcon>
      </Group>
    );
  }

  return null;
};

const AppHeader = () => {
  let history = useHistory();
  const { project } = useAppSelector((state) => state.adminReducer);
  const [loading, setLoading] = useState(false);
  const isMulticonsultUser = useSelector(isLoggedinUserMulticonsultUser);

  useGetProjectsMcappsQuery();
  useGetProjectsMaconomyQuery(undefined, { skip: isMulticonsultUser === false });

  const mergedProjects = useAppSelector(selectMergedProjectList);

  const goToProject = async (maconomy: IMaconomyProject) => {
    setLoading(true);
    const project = await doCreateOrJoinProject(maconomy);
    setLoading(false);

    if (project.useDashboardAsDefaultStartPage) {
      history.push(`/project/${project._id}/dashboard`);
    } else {
      history.push(`/project/${project._id}/apps`);
    }
  };

  return (
    <Group justify="center" px="md" w="100%">
      <Group className={appShellCss.mainPadding} align="center" justify="space-between" wrap="nowrap">
        <Group className={appShellCss.group}>
          <Flex align="center" to="/" component={Link}>
            <span className="text-white font-weight-bold" style={{ fontSize: "16px" }}>
              <i>MCApps</i>
            </span>
          </Flex>
          <McSelect
            w="20rem"
            classNames={{
              input: menuCss.dropdown,
              dropdown: menuCss.dropdown,
              option: menuCss.item,
              search: menuCss.search,
              empty: menuCss.empty,
              thumb: menuCss.thumb,
            }}
            searchable={true}
            leftSection={
              loading ? (
                <Loader size={16} />
              ) : (
                project?.maconomy?.projectNo != null && <IconBriefcaseFilled color="white" size={16} />
              )
            }
            data={mergedProjects?.map((project) => ({ value: String(project.projectNo), label: project.name })) || []}
            value={project?.maconomy?.projectNo != null ? String(project.maconomy.projectNo) : null}
            onChange={(value) => {
              const selectedProject = mergedProjects.find((project) => String(project.projectNo) === value);
              if (selectedProject) {
                goToProject(selectedProject);
              }
            }}
            placeholder="Velg prosjekt"
          />
        </Group>
        <Flex>
          <Flex wrap="nowrap" gap={0} align="center" style={{ userSelect: "none" }}>
            <ReleaseNotesButton />
            <NotificationButton />
            <MyTasksButton />
            <SupportMenu />
          </Flex>
          <UserMenu />
        </Flex>
      </Group>
    </Group>
  );
};

export const SupportMenu = () => {
  const userIsMulticonsult = useAppSelector(selectIsUserMutilconsult);
  const dispatch = useAppDispatch();

  const sendUserMessage = async (message: string, subject: string) => {
    try {
      await doSendUserMessage(subject, message);
      dispatch(setShowSuccessMessageThunk("Takk for tilbakemeldingen.\nVi vil komme tilbake til deg så snart vi kan."));
      modals.close("contact-form-modal");
    } catch (error) {
      dispatch(setShowSuccessMessageThunk("Noe gikk galt! Prøv å sende en e-post i stedet."));
    }
  };

  const openSendMessage = () => {
    modals.open({
      id: "contact-form-modal",
      title: "Kontakt",
      centered: true,
      size: "lg",
      children: <ContactForm sendMessage={sendUserMessage} />,
    });
  };

  const supportItems = {
    teams: {
      name: "Teams",
      link: "https://teams.microsoft.com/l/team/19%3a160b1c8d39394b65b06f619a179f0599%40thread.skype/conversations?groupId=72448e5d-7b2c-4ed5-933b-2d939c4d87a1&tenantId=81ab7478-8d3c-4736-8b54-7f2bfd9fa20e",
    },
    viva: {
      name: "Viva Engage",
      link: "https://web.yammer.com/main/org/multiconsult.no/groups/eyJfdHlwZSI6Ikdyb3VwIiwiaWQiOiIxMTQ1NDg2NjIyNzIifQ",
    },
    email: {
      name: "E-post",
      link: "mailto:mcapps@multiconsult.no",
    },
  };

  return userIsMulticonsult === true ? (
    <Menu
      classNames={{
        dropdown: menuCss.dropdown,
        item: menuCss.item,
        divider: menuCss.divider,
      }}
      withArrow
    >
      <Menu.Target>
        <Tooltip label="Få hjelp" withArrow>
          <Button c="white" size="compact-xl" variant="subtle">
            <IconHelpCircle size={16} />
          </Button>
        </Tooltip>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item leftSection={<IconMail size={16} />} onClick={() => openSendMessage()}>
          {supportItems.email.name}
        </Menu.Item>

        <Menu.Item
          leftSection={<IconBrandTeams size={16} />}
          onClick={() =>
            window.open(supportItems.teams.link, "_blank") || window.location.replace(supportItems.teams.link)
          }
        >
          {supportItems.teams.name}
        </Menu.Item>

        <Menu.Item
          leftSection={<IconSocial size={16} />}
          onClick={() =>
            window.open(supportItems.viva.link, "_blank") || window.location.replace(supportItems.viva.link)
          }
        >
          {supportItems.viva.name}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  ) : (
    <Button leftSection={<IconHelpCircle size={18} />} onClick={() => openSendMessage()}>
      Kontakt support
    </Button>
  );
};

export const UserMenu = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.globalReducer.user);

  const logout = () => {
    dispatch(logoutThunk());
  };

  const openUserSettings = () => {
    modals.open({
      title: "Brukerinnstillinger",
      centered: true,
      size: "40vw",
      radius: "lg",
      children: <UserSettingsModalContent />,
    });
  };

  return (
    <>
      <Menu
        position="bottom-end"
        withArrow
        classNames={{
          dropdown: menuCss.dropdown,
          item: menuCss.item,
          divider: menuCss.divider,
        }}
      >
        <Menu.Target>
          <Button variant="subtle" style={{ padding: 0, background: "none" }}>
            <Avatar id={user._id} name={user.name} size="sm" />
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item onClick={openUserSettings} leftSection={<IconSettings size={16} />}>
            Innstillinger
          </Menu.Item>
          <Menu.Item onClick={logout} leftSection={<IconLogout size={16} />}>
            Logg ut
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </>
  );
};

export const MyTasksButton = () => {
  const user = useAppSelector((state) => state.globalReducer.user);
  const assignedItems = user.activeAssignedItemsCount;
  const navigation = useHistory();

  return (
    <Tooltip label="Mine oppgaver" withArrow>
      <Indicator
        disabled={assignedItems.overdue === 0}
        inline
        size={10}
        offset={7}
        position="top-start"
        color="orange.5"
      >
        <Button c="white" size="compact-xl" variant="subtle" onClick={() => navigation.push("/tasks")}>
          <IconFileCheck size={20} style={{ marginRight: 4 }} />
          {assignedItems?.total > 0 && <Text c="white">{assignedItems.total}</Text>}
        </Button>
      </Indicator>
    </Tooltip>
  );
};
